import type { FC, ReactNode } from 'react';
import React from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { N20A, N30, N500, B50, B400 } from '@atlaskit/theme/colors';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSelectedView } from '../../useSelectedView';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PillsContainer = styled.div({
	paddingLeft: token('space.300', '24px'),
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-html-button -- To migrate as part of go/ui-styling-standard
const PillButton = styled.button<{
	isSelected?: boolean;
}>({
	outline: 'none',
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) =>
		props.isSelected
			? token('color.background.selected', B50)
			: token('color.background.neutral', N20A),
	display: 'inline-flex',
	font: token('font.body'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props) =>
		props.isSelected ? token('color.text.selected', B400) : token('color.text', N500),
	cursor: 'pointer',
	userSelect: 'none',
	whiteSpace: 'nowrap',
	verticalAlign: 'top',
	marginLeft: token('space.100', '8px'),
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: (props) =>
			props.isSelected
				? token('color.background.selected.hovered', B50)
				: token('color.background.neutral.hovered', N30),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:first-of-type': {
		marginLeft: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	padding: token('space.100', '8px'),
	// TODO: Change B400 here to B700 when it becomes available
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	border: (props) =>
		props.isSelected
			? `1px solid ${token('color.border.focused', B400)}`
			: `1px solid ${token('color.background.neutral', N20A)}`,
	borderRadius: '100px',
	height: '32px',
	justifyContent: 'center',
	alignItems: 'center',
	minWidth: '48px',
	'&:focus': {
		outline: `2px solid ${token('color.border.focused', B400)}`,
		borderColor: 'transparent',
	},
});

export type PillFilter = {
	i18n: ReactNode;
	name: string;
};

export type PillProps = {
	pill?: string;
	setPill?: (pill: string) => void;
	pills?: PillFilter[];
};

export const Pills: FC<PillProps> = ({ pill, setPill, pills }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const selectedView = useSelectedView();

	const onClick = (pillButton: PillFilter) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: `${selectedView}Screen`,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: pillButton.name,
			},
		}).fire();

		setPill && setPill(pillButton.name);
	};

	return pills ? (
		<PillsContainer role="tablist">
			{pills.map((pillButton) => (
				<PillButton
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className="select-task-view"
					key={pillButton.name}
					isSelected={pillButton.name === pill}
					onClick={() => onClick(pillButton)}
					role="tab"
					aria-selected={pillButton.name === pill}
				>
					{pillButton.i18n}
				</PillButton>
			))}
		</PillsContainer>
	) : null;
};
